import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Container } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Heart, ArrowLeft, Users, Target, Loader2, Trophy } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return ""; } };

export default function TeamMemberPage() {
  const { slug, memberId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const camp = (await api.get(`/campaigns/${slug}`)).data;
        const res = (await api.get(`/campaigns/${camp.id}/team-members/${memberId}/page`)).data;
        setData(res);
      } catch (e) { setErr("This fundraiser page could not be found."); }
    })();
  }, [slug, memberId]);

  if (err) return <Container><div className="py-24 text-center text-ff-medium" data-testid="member-page-error">{err}</div></Container>;
  if (!data) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-ff-blue" /></div>;

  const { member, campaign, supporters } = data;
  const goal = member.personal_goal || 0;
  const raised = member.amount_raised || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const first = (member.name || "").split(" ")[0];

  return (
    <div className="bg-[#F7F8FA]" data-testid="team-member-page">
      <div className="h-40 w-full bg-cover bg-center sm:h-56" style={{ backgroundImage: `url(${campaign.cover_image || ""})`, backgroundColor: "#143257" }} />
      <Container>
        <div className="mx-auto -mt-16 max-w-2xl">
          <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-lg sm:p-8">
            <Link to={`/fundraisers/${slug}`} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ff-medium hover:text-ff-blue" data-testid="back-to-campaign">
              <ArrowLeft className="h-4 w-4" /> Part of {campaign.title}
            </Link>
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ff-blue text-2xl font-extrabold text-white">{first?.[0]?.toUpperCase() || "?"}</span>
              <div>
                <h1 className="font-heading text-2xl font-extrabold text-ff-blue sm:text-3xl">{member.name}</h1>
                <p className="text-sm text-ff-medium">is fundraising for this cause</p>
              </div>
            </div>
            {member.message && <p className="mt-4 whitespace-pre-line text-ff-dark">{member.message}</p>}

            <div className="mt-6">
              <div className="flex items-end justify-between">
                <div><span className="font-heading text-3xl font-extrabold text-ff-green">{money(raised)}</span>{goal > 0 && <span className="ml-1 text-sm text-ff-medium">raised of {money(goal)}</span>}</div>
                <div className="flex items-center gap-1 text-sm font-bold text-ff-blue"><Users className="h-4 w-4" /> {member.donor_count || 0}</div>
              </div>
              {goal > 0 && (
                <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                  <div className="h-full rounded-full bg-ff-green transition-all" style={{ width: `${pct}%` }} data-testid="member-thermometer" />
                </div>
              )}
            </div>

            {campaign.status === "live" && (
              <Button asChild data-testid="member-donate-btn" className="mt-6 w-full bg-ff-action py-6 text-base font-bold text-white hover:bg-ff-actionDark">
                <Link to={`/fundraisers/${slug}/donate?member=${memberId}`}><Heart className="mr-2 h-5 w-5" /> Donate to {first}</Link>
              </Button>
            )}
            <p className="mt-3 text-center text-xs text-ff-medium">Your gift is credited to {first} and counts toward the team's goal.</p>
          </div>

          <div className="mt-6 rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue"><Trophy className="h-4 w-4 text-ff-amber" /> {first}'s supporters</div>
            {supporters.length === 0 ? (
              <p className="mt-3 text-sm text-ff-medium" data-testid="member-no-supporters">Be the first to support {first}!</p>
            ) : (
              <ul className="mt-3 space-y-2.5" data-testid="member-supporters">
                {supporters.map((s, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 border-b border-ff-border/60 pb-2.5 last:border-0">
                    <div>
                      <div className="text-sm font-semibold text-ff-blue">{s.name}</div>
                      {s.message && <div className="text-xs text-ff-medium">{s.message}</div>}
                    </div>
                    <div className="text-right">
                      {s.amount != null && <div className="text-sm font-bold text-ff-green">{money(s.amount)}</div>}
                      <div className="text-xs text-ff-medium">{fmtDate(s.date)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}
